import React, { useState, useMemo } from "react";
import {
  Button,
  Card,
  CardBody,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Table,
  Modal,
  ModalBody,
} from "reactstrap";
import Select from "components/Select";
import DollarInput from "../DollarInput";
import StatesInput from "components/StatesInput";
import { useUpdateSalesLocation } from "../../api/sales-locations/service";
import { useUpdateTaxAndFee, useDeleteTaxAndFee } from "../../api/tenant/service";
import { toNumber, formatPriceWithDollar } from "utils/currency";
import { getActiveColorStyle } from "utils/colorUtils";
import { getTheme2FormPageDarkSelectStyles } from "utils/theme2MultiSelect";

const SellerFees = ({
  formData,
  setFormData,
  state,
  activeColor = "blue",
  isTheme2 = false,
  customSelectStyles,
  activeColorStyle: activeColorStyleProp,
  salesLocationId,
}) => {
  const activeColorStyle = useMemo(
    () => activeColorStyleProp || getActiveColorStyle(activeColor),
    [activeColorStyleProp, activeColor]
  );
  const selectStyles = useMemo(
    () =>
      customSelectStyles ||
      getTheme2FormPageDarkSelectStyles(activeColorStyle, isTheme2),
    [customSelectStyles, activeColorStyle, isTheme2]
  );
  // Safety check - if formData is undefined, provide default values
  const safeFormData = formData || {};
  const updateSalesLocationMutation = useUpdateSalesLocation();
  const updateTaxAndFeeMutation = useUpdateTaxAndFee({
    onClose: () => { },
    type: "Seller Fee",
  });
  const deleteTaxAndFeeMutation = useDeleteTaxAndFee();

  const [showSellerFeeForm, setShowSellerFeeForm] = useState(false);
  const [currentSellerFee, setCurrentSellerFee] = useState({
    state: state ? state.id : null,
    category: "",
    description: "",
    amount: "$0.00",
    calc_type: "",
  });
  const [editingSellerFeeIndex, setEditingSellerFeeIndex] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDeleteIndex, setSelectedDeleteIndex] = useState(null);

  const handleAddSellerFee = () => {
    let updatedFees;
    const apiFeeData = {
      ...currentSellerFee,
      amount: toNumber(currentSellerFee.amount),
      fee_type: currentSellerFee.calc_type === 'Registration State' ? 'registration' : 'contract',
    };

    if (editingSellerFeeIndex !== null) {
      updatedFees = [...(safeFormData.fees || [])];
      updatedFees[editingSellerFeeIndex] = currentSellerFee;
      setEditingSellerFeeIndex(null);
      setFormData((prev) => ({ ...prev, seller_fees: updatedFees }));

      if (currentSellerFee.id) {
        updateTaxAndFeeMutation.mutate({
          id: currentSellerFee.id,
          data: apiFeeData,
        });
      } else if (salesLocationId) {
        updateSalesLocationMutation.mutate({
          id: salesLocationId,
          data: { seller_fees: apiFeeData },
        });
      }
    } else {
      updatedFees = [...(safeFormData.fees || []), currentSellerFee];
      setFormData((prev) => ({ ...prev, seller_fees: updatedFees }));

      if (salesLocationId) {
        updateSalesLocationMutation.mutate({
          id: salesLocationId,
          data: { seller_fees: apiFeeData },
        });
      }
    }

    setCurrentSellerFee({
      state: state ? state.id : null,
      category: "",
      description: "",
      amount: "$0.00",
      calc_type: "",
    });
    setShowSellerFeeForm(false);
  };

  const handleEditSellerFee = (index) => {
    if (editingSellerFeeIndex === index) {
      // If clicking on same row, close the edit form
      setEditingSellerFeeIndex(null);
      setCurrentSellerFee({
        state: state ? state.id : null,
        category: "",
        description: "",
        amount: "$0.00",
        calc_type: "",
      });
    } else {
      // Open edit form for clicked row
      setCurrentSellerFee((safeFormData.fees || [])[index]);
      setEditingSellerFeeIndex(index);
    }
    setShowSellerFeeForm(false); // Close top form if open
  };

  const handleDeleteClick = (index) => {
    setSelectedDeleteIndex(index);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedDeleteIndex !== null) {
      const deletedFee = (safeFormData.fees || [])[selectedDeleteIndex];
      const updatedFees = (safeFormData.fees || []).filter(
        (_, i) => i !== selectedDeleteIndex
      );
      setFormData((prev) => ({ ...prev, fees: updatedFees }));

      if (deletedFee && (deletedFee.id || deletedFee.documentId)) {
        deleteTaxAndFeeMutation.mutate(deletedFee.id ?? deletedFee.documentId);
      } else if (salesLocationId && deletedFee) {
        // Fallback or newly added local fee without ID
        const apiDeleteData = {
          ...deletedFee,
          amount: toNumber(deletedFee.amount),
          is_deleted: true,
        };
        const updateData = {
          fees: apiDeleteData,
        };
        updateSalesLocationMutation.mutate({
          id: salesLocationId,
          data: updateData,
        });
      }

      setDeleteModalOpen(false);
      setSelectedDeleteIndex(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setSelectedDeleteIndex(null);
  };

  const handleCancelSellerFee = () => {
    setCurrentSellerFee({
      state: state ? state.id : null,
      category: "",
      description: "",
      amount: "$0.00",
      calc_type: "",
    });
    setEditingSellerFeeIndex(null);
    setShowSellerFeeForm(false);
  };

  return (
    <div className="">
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
        }}
      >
        {!showSellerFeeForm && (
          <Button
            size="sm"
            style={{ ...activeColorStyle, color: "white" }}
            onClick={() => {
              setEditingSellerFeeIndex(null); // Close any open edit form
              setShowSellerFeeForm(true);
            }}
          >
            Add Fee
          </Button>
        )}
      </div>

      {showSellerFeeForm && (
        <>
          <Row>
            <Col md="6">
              <FormGroup>
                <Label className="form-control-label">Fee State:</Label>
                <StatesInput
                  name="state"
                  activeColor={activeColor}
                  styles={selectStyles}
                  className="react-select"
                  value={currentSellerFee.state}
                  onChange={(stateObject) => {
                    // stateObject = { id, abbreviation, name } or null
                    setCurrentSellerFee((prev) => ({
                      ...prev,
                      state: stateObject
                        ? stateObject.id
                        : null,
                    }));
                  }}
                  placeholder="Select State"
                />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label className="form-control-label">Fee Category:</Label>
                <Select
                  activeColor={activeColor}
                  styles={selectStyles}
                  className="react-select"
                  name="category"
                  value={
                    currentSellerFee.category
                      ? {
                        value: currentSellerFee.category,
                        label: currentSellerFee.category,
                      }
                      : null
                  }
                  onChange={(option) =>
                    setCurrentSellerFee((prev) => ({
                      ...prev,
                      category: option?.value || "",
                    }))
                  }
                  options={[
                    { value: "Document Fee", label: "Document Fee" },
                    { value: "Title Fee", label: "Title Fee" },
                    { value: "License Fee", label: "License Fee" },
                    { value: "Registration Fee", label: "Registration Fee" },
                    { value: "State Inspection Fee", label: "State Inspection Fee" },
                    { value: "Plate Transfer Fee", label: "Plate Transfer Fee" },
                    { value: "Deputy Service Fee", label: "Deputy Service Fee" },
                    { value: "Transportation Fee", label: "Transportation Fee" },
                    { value: "Service Fee", label: "Service Fee" },
                    { value: "Inspection Station Fee", label: "Inspection Station Fee" },
                    { value: "State Admin Fee", label: "State Admin Fee" },
                  ]}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="6">
              <FormGroup>
                <Label className="form-control-label">Fee Description:</Label>
                <Input
                  type="text"
                  value={currentSellerFee.description}
                  onChange={(e) =>
                    setCurrentSellerFee((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label className="form-control-label">Fee Amount:</Label>
                <DollarInput
                  value={currentSellerFee.amount}
                  onChange={(value) =>
                    setCurrentSellerFee((prev) => ({
                      ...prev,
                      amount: value,
                    }))
                  }
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="6">
              <FormGroup>
                <Label className="form-control-label">Calculation Type:</Label>
                <Select
                  activeColor={activeColor}
                  styles={selectStyles}
                  className="react-select"
                  value={
                    currentSellerFee.calc_type
                      ? {
                        value: currentSellerFee.calc_type,
                        label: currentSellerFee.calc_type,
                      }
                      : null
                  }
                  onChange={(option) =>
                    setCurrentSellerFee((prev) => ({
                      ...prev,
                      calc_type: option?.value || "",
                    }))
                  }
                  options={[
                    { value: "Registration State", label: "Registration State" },
                    { value: "Contract State", label: "Contract State" },
                  ]}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col className="text-right">
              <Button
                size="sm"
                onClick={handleCancelSellerFee}
                style={{ marginRight: "10px" }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                style={{ ...activeColorStyle, color: "white" }}
                onClick={handleAddSellerFee}
              >
                {editingSellerFeeIndex !== null ? "Update" : "Add"}
              </Button>
            </Col>
          </Row>
        </>
      )}

      {safeFormData.fees?.length === 0 ? (
        <div style={{ textAlign: "center", padding: "20px", color: "#999" }}>
          No fees added yet
        </div>
      ) : (
        <Table responsive>
          <thead className="text-primary">
            <tr>
              <th>State</th>
              <th>Category</th>
              <th>Description</th>
              <th>Amount</th>
              <th>Fee Type</th>
              <th className="text-right"></th>
            </tr>
          </thead>
          <tbody>
            {safeFormData.fees?.map((fee, idx) => (
              <React.Fragment key={idx}>
                <tr onClick={() => handleEditSellerFee(idx)}>
                  <td>{fee.state?.abbreviation}</td>
                  <td>{fee.category}</td>
                  <td>{fee.description}</td>
                  <td>{formatPriceWithDollar(fee.amount)}</td>
                  <td>
                    {fee.calc_type.charAt(0).toUpperCase() +
                      fee.calc_type.slice(1)}
                  </td>
                  <td className="text-right">
                    <div className="actions-right">
                      <Button
                        color="warning"
                        size="sm"
                        className="btn-icon btn-link like"
                        onClick={() => handleEditSellerFee(idx)}
                      >
                        <i className="tim-icons icon-pencil" />
                      </Button>
                      <Button
                        color="danger"
                        size="sm"
                        className="btn-icon btn-link like"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(idx);
                        }}
                      >
                        <i className="tim-icons icon-simple-remove" />
                      </Button>
                    </div>
                  </td>
                </tr>
                {editingSellerFeeIndex === idx && (
                  <tr>
                    <td colSpan="7">
                      <Row>
                        <Col md="6">
                          <FormGroup>
                            <Label className="form-control-label">
                              Fee State:
                            </Label>
                            <StatesInput
                              name="state"
                              activeColor={activeColor}
                              styles={selectStyles}
                              className="react-select"
                              value={currentSellerFee.state}
                              onChange={(stateObject) => {
                                setCurrentSellerFee((prev) => ({
                                  ...prev,
                                  state: stateObject
                                    ? stateObject.id
                                    : null,
                                }));
                              }}
                              placeholder="Select State"
                            />
                          </FormGroup>
                        </Col>
                        <Col md="6">
                          <FormGroup>
                            <Label className="form-control-label">
                              Fee Category:
                            </Label>
                            <Select
                              activeColor={activeColor}
                              styles={selectStyles}
                              className="react-select"
                              name="category"
                              value={
                                currentSellerFee.category
                                  ? {
                                    value: currentSellerFee.category,
                                    label: currentSellerFee.category,
                                  }
                                  : null
                              }
                              onChange={(option) =>
                                setCurrentSellerFee((prev) => ({
                                  ...prev,
                                  category: option?.value || "",
                                }))
                              }
                              options={[
                                { value: "Document Fee", label: "Document Fee" },
                                { value: "Title Fee", label: "Title Fee" },
                                { value: "License Fee", label: "License Fee" },
                                { value: "Registration Fee", label: "Registration Fee" },
                                { value: "State Inspection Fee", label: "State Inspection Fee" },
                                { value: "Plate Transfer Fee", label: "Plate Transfer Fee" },
                                { value: "Deputy Service Fee", label: "Deputy Service Fee" },
                                { value: "Transportation Fee", label: "Transportation Fee" },
                                { value: "Service Fee", label: "Service Fee" },
                                { value: "Inspection Station Fee", label: "Inspection Station Fee" },
                                { value: "State Admin Fee", label: "State Admin Fee" },
                              ]}
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                      <Row>
                        <Col md="6">
                          <FormGroup>
                            <Label className="form-control-label">
                              Fee Description:
                            </Label>
                            <Input
                              type="text"
                              value={currentSellerFee.description}
                              onChange={(e) =>
                                setCurrentSellerFee((prev) => ({
                                  ...prev,
                                  description: e.target.value,
                                }))
                              }
                            />
                          </FormGroup>
                        </Col>
                        <Col md="6">
                          <FormGroup>
                            <Label className="form-control-label">
                              Fee Amount:
                            </Label>
                            <DollarInput
                              value={currentSellerFee.amount}
                              onChange={(value) =>
                                setCurrentSellerFee((prev) => ({
                                  ...prev,
                                  amount: value,
                                }))
                              }
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                      <Row>
                        <Col md="6">
                          <FormGroup>
                            <Label className="form-control-label">
                              Calculation Type:
                            </Label>
                            <Select
                              activeColor={activeColor}
                              styles={selectStyles}
                              className="react-select"
                              value={
                                currentSellerFee.calc_type
                                  ? {
                                    value: currentSellerFee.calc_type,
                                    label: currentSellerFee.calc_type,
                                  }
                                  : null
                              }
                              onChange={(option) =>
                                setCurrentSellerFee((prev) => ({
                                  ...prev,
                                  calc_type: option?.value || "",
                                }))
                              }
                              options={[
                                {
                                  value: "Registration State",
                                  label: "Registration State",
                                },
                                { value: "Contract State", label: "Contract State" },
                              ]}
                              isClearable
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                      <Row>
                        <Col className="text-right">
                          <Button
                            size="sm"
                            onClick={handleCancelSellerFee}
                            style={{ marginRight: "10px" }}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            style={{ ...activeColorStyle, color: "white" }}
                            onClick={handleAddSellerFee}
                          >
                            Update
                          </Button>
                        </Col>
                      </Row>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </Table>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        toggle={handleCancelDelete}
        centered={false}
        style={{ marginTop: "100px" }}
      >
        <div className="modal-header justify-content-center">
          <button
            aria-hidden={true}
            className="close"
            data-dismiss="modal"
            type="button"
            onClick={handleCancelDelete}
          >
            <i className="tim-icons icon-simple-remove" />
          </button>
          <h6 className="title title-up">Delete Seller Fee</h6>
        </div>
        <ModalBody className="text-center">
          <p>Are you sure you want to delete this seller fee?</p>
        </ModalBody>
        <div className="modal-footer">
          <Button
            color="secondary"
            type="button"
            onClick={handleCancelDelete}
            disabled={updateSalesLocationMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            style={{
              background:
                "linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)",
              backgroundImage:
                "linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)",
              borderColor: "#fd5d93",
              color: "white",
            }}
            color="danger"
            type="button"
            onClick={handleConfirmDelete}
            disabled={updateSalesLocationMutation.isPending}
          >
            {updateSalesLocationMutation.isPending ? (
              <>
                <span
                  className="spinner-border spinner-border-sm mr-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Deleting...
              </>
            ) : (
              "Yes, Delete"
            )}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default SellerFees;
